/**
 * COM-9.12 — is the contract-storage sweeper alive, and is it actually deleting?
 *
 * The sweeper and its run table both existed and worked from the day they were
 * written with NOTHING CALLING THEM, which is the AD-33 defect the design guards
 * against and which three scheduled jobs in this codebase have already died of.
 * This is the reading half: the schedule is
 * `20260828b6_com_9_12_contract_storage_sweep_health.sql`.
 *
 * ⚠️ THE STATE THAT MATTERS IS `dry_run_only`, AND IT IS WHY THIS IS NOT JUST A
 * "DID IT RUN" CHECK. `run_sweep` defaults to a dry run so a caller has to ask
 * for deletion. A cron that forgets the flag runs cleanly forever, records a
 * successful pass every day, and reaps nothing. Every "is it running" check calls
 * that healthy. It is the same shape as COM-8.9's zero and COM-9.13's
 * `not_needed`: an answer that looks like health and is not.
 *
 * ⚠️ AND THE DELETION FIGURES ARE ONLY MEANINGFUL FROM A LIVE RUN. The run table
 * populates `orphan_seal_deleted` on a DRY RUN too, where it means "would have
 * deleted". The database function filters `NOT dry_run` before summing; this
 * module must not undo that by summing anything itself.
 *
 * Measured 2026-08-28 while driving the endpoint: the bucket holds 188 objects
 * and 0 are orphaned (2 placeholders, 166 legacy-shape, 15 live seal artefacts,
 * 6 sources in use). The story's own premise of "504 objects, 316 orphaned" is
 * stale; no bucket in the database holds 504. Scheduling this is preventative.
 */

export interface ContractSweepHealth {
  never_run: boolean;
  last_run_at: string | null;
  last_error_at: string | null;
  last_error: string | null;
  runs_last_24h: number;
  dry_run_only: boolean;
  last_examined: number | null;
  last_deleted: number | null;
  total_deleted: number;
  healthy: boolean;
}

/**
 * ⚠️ `reporting_only` IS ITS OWN STATE AND NOT A FLAVOUR OF `stalled`, because the
 * fix is different: a stalled sweeper needs its schedule looked at, a reporting
 * one needs a flag changed on the caller. Collapsing them sends somebody to the
 * wrong place.
 */
export type ContractSweepState = 'never_run' | 'failing' | 'reporting_only' | 'stalled' | 'ok';

export interface ContractSweepVerdict {
  state: ContractSweepState;
  label: string;
  detail: string;
}

export const CONTRACT_SWEEP_COPY = {
  title: 'Contract storage sweeper',
  description:
    'Every replaced document, failed send and deleted contract leaves a PDF behind in the contracts bucket. This reaps them daily. It is reported here because a sweeper nobody can see is the one that stops without anybody noticing.',

  /** ⚠️ The sentence that names the trap this card exists for. */
  neverDeletes:
    'It is running and it is deleting nothing. A dry run reports what it would remove and removes none of it, so every "is it running" check reads this as healthy.',

  scope:
    'The contracts bucket spans every account, so this is one mechanism for the whole product rather than a per-account setting.',
} as const;

function whenText(iso: string | null): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'never';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  return `${Math.round(hours / 24)} days ago`;
}

/**
 * Ordered most-conclusive first: something that has never happened outranks
 * something that failed, which outranks something running in the wrong mode,
 * which outranks something that has merely gone quiet.
 */
export function contractSweepVerdict(h: ContractSweepHealth): ContractSweepVerdict {
  if (h.never_run) {
    return {
      state: 'never_run',
      label: 'Has never run',
      detail:
        'The sweeper exists and works, and nothing has ever called it. Nothing is being reaped from the contracts bucket at all.',
    };
  }

  if (h.last_error_at && (!h.last_run_at || h.last_error_at > h.last_run_at)) {
    return {
      state: 'failing',
      label: 'Last pass failed',
      detail: `The most recent pass errored ${whenText(h.last_error_at)}${
        h.last_error ? `: ${h.last_error}` : '.'
      } The last clean pass was ${whenText(h.last_run_at)}.`,
    };
  }

  if (h.dry_run_only) {
    return {
      state: 'reporting_only',
      label: 'Reporting only, deleting nothing',
      detail: `${CONTRACT_SWEEP_COPY.neverDeletes} Last pass ${whenText(h.last_run_at)}, over ${
        h.last_examined ?? 0
      } objects.`,
    };
  }

  if (!h.healthy) {
    return {
      state: 'stalled',
      label: 'Sweeper is stale',
      detail: `No clean deleting pass in the last 26 hours, against a daily schedule. Last one ${whenText(
        h.last_run_at,
      )}. Passes in 24h: ${h.runs_last_24h}.`,
    };
  }

  return {
    state: 'ok',
    label: 'Sweeper healthy',
    detail: `Last pass ${whenText(h.last_run_at)}, over ${h.last_examined ?? 0} objects, removing ${
      h.last_deleted ?? 0
    }. ${h.total_deleted} object${h.total_deleted === 1 ? '' : 's'} reaped in total.`,
  };
}

/** Green only for `ok`; everything else is a problem with a different fix. */
export function contractSweepIsHealthy(state: ContractSweepState): boolean {
  return state === 'ok';
}
